// Network configuration for frontend
// Should match the backend configuration

export const CHAIN_IDS = {
  Ethereum: 1,
  Arbitrum: 42161,
  Base: 8453,
  Polygon: 137,
  BNB: 56,
  Avalanche: 43114,
  Solana: 'mainnet-beta',
  Bitcoin: 'mainnet',
  Tron: 'mainnet',
  Polkadot: 'AssetHub'
} as const;

// Default network icon for fallback
const DEFAULT_NETWORK_ICON = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiM2MzY2RjEiLz4KPHBhdGggZD0iTTE2IDI0QzIwLjQxODMgMjQgMjQgMjAuNDE4MyAyNCAxNkMyNCAxMS41ODE3IDIwLjQxODMgOCAxNiA4QzExLjU4MTcgOCA4IDExLjU4MTcgOCAxNkM4IDIwLjQxODMgMTEuNTgxNyAyNCAxNiAyNCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+';

export const CHAIN_CONFIG = {
  Ethereum: {
    chainId: 1,
    name: 'Ethereum',
    icon: '/icons/ethereum.svg',
    iconFallback: DEFAULT_NETWORK_ICON,
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    blockExplorer: 'https://etherscan.io',
    enabled: import.meta.env.VITE_ENABLE_ETHEREUM === 'true'
  },
  Arbitrum: {
    chainId: 42161,
    name: 'Arbitrum One',
    icon: '/icons/arbitrum.svg',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    blockExplorer: 'https://arbiscan.io',
    enabled: import.meta.env.VITE_ENABLE_ARBITRUM === 'true'
  },
  Base: {
    chainId: 8453,
    name: 'Base',
    icon: '/icons/base.svg',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    blockExplorer: 'https://basescan.org',
    enabled: import.meta.env.VITE_ENABLE_BASE === 'true'
  },
  Polygon: {
    chainId: 137,
    name: 'Polygon',
    icon: '/icons/polygon.svg',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
    blockExplorer: 'https://polygonscan.com',
    enabled: import.meta.env.VITE_ENABLE_POLYGON === 'true'
  },
  BNB: {
    chainId: 56,
    name: 'BNB Chain',
    icon: '/icons/bnb.svg',
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    blockExplorer: 'https://bscscan.com',
    enabled: import.meta.env.VITE_ENABLE_BNB === 'true'
  },
  Avalanche: {
    chainId: 43114,
    name: 'Avalanche C-Chain',
    icon: '/icons/avalanche.svg',
    nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 },
    blockExplorer: 'https://snowtrace.io',
    enabled: import.meta.env.VITE_ENABLE_AVALANCHE === 'true'
  },
  Solana: {
    cluster: 'mainnet-beta',
    name: 'Solana',
    icon: '/icons/solana.svg',
    nativeCurrency: { name: 'SOL', symbol: 'SOL', decimals: 9 },
    blockExplorer: 'https://solscan.io',
    enabled: import.meta.env.VITE_ENABLE_SOLANA === 'true'
  },
  Bitcoin: {
    network: 'mainnet',
    name: 'Bitcoin',
    icon: '/icons/bitcoin.svg',
    nativeCurrency: { name: 'Bitcoin', symbol: 'BTC', decimals: 8 },
    blockExplorer: 'https://blockstream.info',
    enabled: import.meta.env.VITE_ENABLE_BITCOIN === 'true'
  },
  Tron: {
    network: 'mainnet',
    name: 'Tron',
    icon: '/icons/tron.svg',
    nativeCurrency: { name: 'TRX', symbol: 'TRX', decimals: 6 },
    blockExplorer: 'https://tronscan.org',
    enabled: false // Not supported via AppKit yet
  },
  Polkadot: {
    network: 'AssetHub',
    name: 'Polkadot AssetHub',
    icon: '/icons/polkadot.svg',
    nativeCurrency: { name: 'DOT', symbol: 'DOT', decimals: 10 },
    blockExplorer: 'https://polkadot.subscan.io',
    enabled: false // Not supported via AppKit yet
  }
} as const;

// Token configuration
export const TOKEN_CONFIG = {
  USDC: {
    symbol: 'USDC',
    icon: '/icons/usdc.svg',
    addresses: {
      Ethereum: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      Base: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      Polygon: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
      BNB: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
      Arbitrum: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
      Avalanche: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
      Solana: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'
    },
    decimals: {
      default: 6,
      BNB: 18 // USDC on BSC has 18 decimals
    }
  },
  USDT: {
    symbol: 'USDT',
    icon: '/icons/usdt.svg',
    addresses: {
      Ethereum: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      Polygon: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      BNB: '0x55d398326f99059fF775485246999027B3197955',
      Arbitrum: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
      Avalanche: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7',
      Tron: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
      Solana: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB'
    },
    decimals: {
      default: 6,
      BNB: 18 // USDT on BSC has 18 decimals
    }
  },
  BAT: {
    symbol: 'BAT',
    icon: '/icons/bat.svg',
    addresses: {
      Ethereum: '0x0D8775F648430679A709E98d2b0Cb6250d2887EF',
      Polygon: '0x3Cef98bb43d732E2F285eE605a8158cDE967D219',
      Solana: 'EPeUFDgHRxs9xxEPVaL6kfGQvCon7jmAWKVUHuux1Tpz'
    },
    decimals: {
      default: 18,
      Solana: 8
    }
  },
  BUSD: {
    symbol: 'BUSD',
    icon: '/icons/busd.svg',
    addresses: {
      BNB: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
      Ethereum: '0x4Fabb145d64652a948d72533023f6E7A623C7C53'
    },
    decimals: {
      default: 18
    }
  }
} as const;

// Helper functions
export function getSupportedTokensForNetwork(network: string): Array<{
  symbol: string;
  address: string;
  decimals: number;
  icon: string;
}> {
  const supportedTokens = [];
  
  for (const [symbol, config] of Object.entries(TOKEN_CONFIG)) {
    const address = config.addresses[network as keyof typeof config.addresses];
    if (address) {
      const decimals = config.decimals[network as keyof typeof config.decimals] || config.decimals.default;
      supportedTokens.push({
        symbol,
        address,
        decimals,
        icon: config.icon
      });
    }
  }
  
  return supportedTokens;
}

export function getNetworkConfig(network: string) {
  return CHAIN_CONFIG[network as keyof typeof CHAIN_CONFIG];
}

export function getEnabledNetworks() {
  return Object.entries(CHAIN_CONFIG)
    .filter(([_, config]) => config.enabled)
    .map(([network, config]) => ({ network, ...config }));
}

// Helper to get network icon with fallback
export function getNetworkIcon(network: string): string {
  const config = CHAIN_CONFIG[network as keyof typeof CHAIN_CONFIG];
  if (!config) return DEFAULT_NETWORK_ICON;
  
  // Return the icon URL, will use fallback if main icon fails to load
  return config.icon || DEFAULT_NETWORK_ICON;
}

